import { useEffect, useState } from "react";
import { ArrowLeft, CalendarClock, MonitorX, Radio } from "lucide-react";
import { Link, useParams } from "react-router-dom";
import { Alert } from "../components/Alert";
import { AppShell } from "../components/AppShell";
import { Avatar } from "../components/Avatar";
import { Badge, RoomStatusBadge } from "../components/Badge";
import { Button, buttonStyles } from "../components/Button";
import { EmptyState } from "../components/EmptyState";
import { FullPageLoader } from "../components/Spinner";
import { useAuth } from "../context/AuthContext";
import { ApiError, api, errorMessage } from "../lib/api";
import {
  formatDateTime,
  formatDuration,
  formatResolution,
} from "../lib/format";
import type { Room } from "../lib/types";
import { HostRoom } from "./room/HostRoom";
import { ViewerRoom } from "./room/ViewerRoom";

function EndedRoom({ room }: { room: Room }) {
  return (
    <AppShell wide>
      <div className="mx-auto max-w-2xl py-12">
        <div className="rounded-2xl border border-white/[0.07] bg-[#0c0e14]/80 p-8 text-center sm:p-10">
          <div className="mx-auto flex size-14 items-center justify-center rounded-2xl border border-white/[0.08] bg-white/[0.03]">
            <MonitorX className="size-6 text-zinc-500" />
          </div>
          <Badge className="mt-5">Завершена</Badge>
          <h1 className="mt-4 text-balance text-2xl font-semibold tracking-[-0.03em] text-white">
            {room.title}
          </h1>
          <p className="mt-2 text-[13px] text-zinc-500">
            Трансляция завершена. Запись не сохранялась.
          </p>

          <div className="mt-7 grid gap-3 text-left sm:grid-cols-3">
            <div className="rounded-xl border border-white/[0.06] bg-white/[0.015] p-4">
              <p className="text-[10px] uppercase tracking-widest text-zinc-600">
                Ведущий
              </p>
              <p className="mt-1 truncate text-[12px] text-zinc-300">
                {room.owner.displayName}
              </p>
            </div>
            <div className="rounded-xl border border-white/[0.06] bg-white/[0.015] p-4">
              <p className="text-[10px] uppercase tracking-widest text-zinc-600">
                Длительность
              </p>
              <p className="mt-1 text-[12px] text-zinc-300">
                {formatDuration(room.startedAt, room.endedAt)}
              </p>
            </div>
            <div className="rounded-xl border border-white/[0.06] bg-white/[0.015] p-4">
              <p className="text-[10px] uppercase tracking-widest text-zinc-600">
                Параметры
              </p>
              <p className="mt-1 font-mono text-[12px] text-zinc-300">
                {room.settings
                  ? `${formatResolution(room.settings.width, room.settings.height)} · ${room.settings.fps} FPS`
                  : "—"}
              </p>
            </div>
          </div>

          <div className="mt-7 flex flex-wrap items-center justify-center gap-2">
            <Link to="/dashboard" className={buttonStyles("primary", "md")}>
              <ArrowLeft className="size-4" />В кабинет
            </Link>
          </div>
          <p className="mt-5 inline-flex items-center gap-1.5 text-[10px] text-zinc-700">
            <CalendarClock className="size-3.5" />
            Завершено {formatDateTime(room.endedAt)}
          </p>
        </div>
      </div>
    </AppShell>
  );
}

function WaitingRoom({ room }: { room: Room }) {
  return (
    <AppShell wide>
      <div className="mx-auto max-w-xl py-16">
        <EmptyState
          icon={Radio}
          title="Трансляция ещё не началась"
          description={`Комната «${room.title}» создана, но ведущий ${room.owner.displayName} пока не запустил поток. Вернитесь в кабинет — активные комнаты появляются там автоматически.`}
          action={
            <Link to="/dashboard" className={buttonStyles("secondary", "md")}>
              <ArrowLeft className="size-4" />В кабинет
            </Link>
          }
        />
      </div>
    </AppShell>
  );
}

export function RoomPage() {
  const { id } = useParams<{ id: string }>();
  const { user } = useAuth();
  const [room, setRoom] = useState<Room | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    if (!id) {
      setNotFound(true);
      setLoading(false);
      return;
    }
    let disposed = false;
    setLoading(true);
    setError(null);
    setNotFound(false);
    void (async () => {
      try {
        const response = await api.getRoom(id);
        if (!disposed) setRoom(response.room);
      } catch (caught) {
        if (disposed) return;
        if (caught instanceof ApiError && caught.status === 404) {
          setNotFound(true);
        } else {
          setError(errorMessage(caught));
        }
      } finally {
        if (!disposed) setLoading(false);
      }
    })();
    return () => {
      disposed = true;
    };
  }, [id]);

  const isOwner = Boolean(room && user && room.owner.id === user.id);

  if (loading) {
    return (
      <AppShell wide>
        <FullPageLoader label="Открываем комнату…" />
      </AppShell>
    );
  }

  if (notFound || !room) {
    return (
      <AppShell wide>
        <div className="mx-auto max-w-lg py-20">
          <EmptyState
            icon={MonitorX}
            title="Комната не найдена"
            description="Возможно, ссылка устарела, комната удалена или у вас нет доступа."
            action={
              <Link to="/dashboard" className={buttonStyles("secondary", "md")}>
                <ArrowLeft className="size-4" />В кабинет
              </Link>
            }
          />
        </div>
      </AppShell>
    );
  }

  if (error) {
    return (
      <AppShell wide>
        <div className="mx-auto max-w-lg py-16">
          <Alert tone="error" title="Не удалось открыть комнату">
            {error}
          </Alert>
          <Button
            variant="secondary"
            className="mt-4"
            onClick={() => window.location.reload()}
          >
            Повторить
          </Button>
        </div>
      </AppShell>
    );
  }

  if (room.status === "ended") {
    return <EndedRoom room={room} />;
  }

  if (!isOwner && room.status === "waiting") {
    return <WaitingRoom room={room} />;
  }

  if (isOwner) {
    return (
      <AppShell wide>
        <HostRoom
          room={room}
          onRoomChange={setRoom}
          onEnded={(ended) => setRoom(ended)}
        />
      </AppShell>
    );
  }

  return (
    <AppShell wide>
      <ViewerRoom
        room={room}
        onRoomChange={setRoom}
        onEnded={(ended) => setRoom(ended)}
      />
    </AppShell>
  );
}
